import React, { useState, useEffect, useMemo } from 'react';
import { App, Checkbox, InputNumber, Button, Row, Col, Space, Typography, Table, Tag, Tabs, Badge, Tooltip, Descriptions, Divider, List, Card } from 'antd';
import { SearchOutlined, CloseOutlined, InfoCircleOutlined, UpOutlined, DownOutlined } from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { apiClient } from '../../utils/api';
import { StandardCard, PageTitle } from '../common';
import { theme } from '../../theme';

const { Text } = Typography;

interface FilterValues {
  CALIDAD_NUMERICA: { checked: boolean; desde: number | null; hasta: number | null };
  SCI: { checked: boolean; desde: number | null; hasta: number | null };
  MST: { checked: boolean; desde: number | null; hasta: number | null };
  UHML: { checked: boolean; desde: number | null; hasta: number | null };
  ML: { checked: boolean; desde: number | null; hasta: number | null };
  UI: { checked: boolean; desde: number | null; hasta: number | null };
  STR: { checked: boolean; desde: number | null; hasta: number | null };
  ELG: { checked: boolean; desde: number | null; hasta: number | null };
  MIC: { checked: boolean; desde: number | null; hasta: number | null };
  AMT: { checked: boolean; desde: number | null; hasta: number | null };
  RD: { checked: boolean; desde: number | null; hasta: number | null };
  B: { checked: boolean; desde: number | null; hasta: number | null };
  CG: { checked: boolean; desde: number | null; hasta: number | null };
  T_CNT: { checked: boolean; desde: number | null; hasta: number | null };
  T_AREA: { checked: boolean; desde: number | null; hasta: number | null };
  MR: { checked: boolean; desde: number | null; hasta: number | null };
  SFI: { checked: boolean; desde: number | null; hasta: number | null };
  TRID: { checked: boolean; desde: number | null; hasta: number | null };
}

// Definir categorías de filtros HVI
const filterCategories = {
  calidad: {
    label: 'Calidad',
    shortLabel: 'Calidad',
    fields: ['CALIDAD_NUMERICA'] as (keyof FilterValues)[],
    icon: '📊'
  },
  fisicas: {
    label: 'Propiedades Físicas',
    shortLabel: 'Físicas',
    fields: ['SCI', 'MST', 'UHML', 'ML', 'UI'] as (keyof FilterValues)[],
    icon: '🔬'
  },
  mecanicas: {
    label: 'Propiedades Mecánicas',
    shortLabel: 'Mecánicas',
    fields: ['STR', 'ELG', 'MIC'] as (keyof FilterValues)[],
    icon: '⚙️'
  },
  avanzadas: {
    label: 'Propiedades Avanzadas',
    shortLabel: 'Avanzadas',
    fields: ['AMT', 'RD', 'B', 'CG', 'T_CNT', 'T_AREA', 'MR', 'SFI', 'TRID'] as (keyof FilterValues)[],
    icon: '🔍'
  }
};

// Descripciones de campos HVI para tooltips
const hviDescriptions: Record<string, string> = {
  CALIDAD_NUMERICA: 'Calidad numérica del algodón',
  SCI: 'Spinning Consistency Index - Índice de consistencia para hilado',
  MST: 'Maturity - Madurez de la fibra',
  UHML: 'Upper Half Mean Length - Longitud promedio de la mitad superior',
  ML: 'Mean Length - Longitud promedio',
  UI: 'Uniformity Index - Índice de uniformidad',
  STR: 'Strength - Resistencia de la fibra',
  ELG: 'Elongation - Elongación',
  MIC: 'Micronaire - Finura y madurez',
  AMT: 'Area Mean - Área promedio',
  RD: 'Reflectance Degree - Grado de reflectancia',
  B: 'Yellowness - Amarillez',
  CG: 'Color Grade - Grado de color',
  T_CNT: 'Trash Count - Conteo de basura',
  T_AREA: 'Trash Area - Área de basura',
  MR: 'Maturity Ratio - Relación de madurez',
  SFI: 'Short Fiber Index - Índice de fibra corta',
  TRID: 'Trash ID - Identificación de basura'
};

const Loteo: React.FC = () => {
  const { message } = App.useApp();
  const { getAccessTokenSilently, user } = useAuth0();
  const [rangosHVI, setRangosHVI] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [fardosSeleccionados, setFardosSeleccionados] = useState<Set<number>>(new Set());
  const [numeroLote, setNumeroLote] = useState<number | null>(null);
  const [filtrosExpandidos, setFiltrosExpandidos] = useState(true);
  
  const [filters, setFilters] = useState<FilterValues>({
    CALIDAD_NUMERICA: { checked: false, desde: null, hasta: null },
    SCI: { checked: false, desde: null, hasta: null },
    MST: { checked: false, desde: null, hasta: null },
    UHML: { checked: false, desde: null, hasta: null },
    ML: { checked: false, desde: null, hasta: null },
    UI: { checked: false, desde: null, hasta: null },
    STR: { checked: false, desde: null, hasta: null },
    ELG: { checked: false, desde: null, hasta: null },
    MIC: { checked: false, desde: null, hasta: null },
    AMT: { checked: false, desde: null, hasta: null },
    RD: { checked: false, desde: null, hasta: null },
    B: { checked: false, desde: null, hasta: null },
    CG: { checked: false, desde: null, hasta: null },
    T_CNT: { checked: false, desde: null, hasta: null },
    T_AREA: { checked: false, desde: null, hasta: null },
    MR: { checked: false, desde: null, hasta: null },
    SFI: { checked: false, desde: null, hasta: null },
    TRID: { checked: false, desde: null, hasta: null },
  });

  // Función para validar rangos (desde <= hasta)
  const isValidRange = (desde: number | null, hasta: number | null): boolean => {
    if (desde === null || hasta === null) return true;
    return desde <= hasta;
  };

  // Obtener filtros activos con validación
  const activeFilters = useMemo(() => {
    return Object.entries(filters)
      .filter(([_, filter]) => filter.checked)
      .map(([field, filter]) => ({
        field: field as keyof FilterValues,
        ...filter,
        isValid: isValidRange(filter.desde, filter.hasta)
      }));
  }, [filters]);

  // Vista previa de resultados estimados (Propuesta 5)
  const estimatedResults = useMemo(() => {
    if (activeFilters.length === 0) return null;
    
    const invalidFilters = activeFilters.filter(f => !f.isValid);
    if (invalidFilters.length > 0) return null;

    // Estimación simple basada en cantidad de filtros activos
    // Más filtros = menos resultados esperados
    const baseEstimate = 1000;
    const reductionFactor = Math.pow(0.7, activeFilters.length);
    const estimated = Math.round(baseEstimate * reductionFactor);
    
    return {
      estimated,
      confidence: activeFilters.length <= 3 ? 'alta' : activeFilters.length <= 5 ? 'media' : 'baja'
    };
  }, [activeFilters]);

  const handleCheckboxChange = (field: keyof FilterValues, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], checked }
    }));
  };

  const handleDesdeChange = (field: keyof FilterValues, value: number | null) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], desde: value }
    }));
  };

  const handleHastaChange = (field: keyof FilterValues, value: number | null) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], hasta: value }
    }));
  };

  const handleClearFilter = (field: keyof FilterValues) => {
    setFilters(prev => ({
      ...prev,
      [field]: { ...prev[field], checked: false, desde: null, hasta: null }
    }));
  };

  const handleClearAllFilters = () => {
    const newFilters = { ...filters };
    Object.keys(newFilters).forEach(field => {
      newFilters[field as keyof FilterValues] = {
        checked: false,
        desde: null,
        hasta: null
      };
    });
    setFilters(newFilters);
  };

  const handleSearch = async () => {
    const filtrosActivos = Object.entries(filters).filter(([_, filter]) => filter.checked);

    if (filtrosActivos.length === 0) {
      message.warning('Por favor, seleccione al menos un filtro para buscar');
      return;
    }

    // Contraer los filtros al iniciar la búsqueda
    setFiltrosExpandidos(false);

    // Validar rangos antes de buscar
    const invalidFilters = filtrosActivos.filter(([_, filter]) => 
      !isValidRange(filter.desde, filter.hasta)
    );

    if (invalidFilters.length > 0) {
      message.error('Por favor, corrija los rangos inválidos (Desde debe ser menor o igual a Hasta)');
      return;
    }

    // Validar que tenemos los datos necesarios antes de intentar la petición
    if (!user?.id_empresa) {
      message.error('No se pudo obtener la información de la empresa. Por favor, recargue la página.');
      return;
    }

    if (!process.env.REACT_APP_CLASIFICACION_URL) {
      message.error('Error de configuración: URL del servicio no disponible');
      return;
    }
    
    try {
      setBuscando(true);
      
      const id_empresa = user.id_empresa;
      let token;
      
      try {
        token = await getAccessTokenSilently();
      } catch (authError: any) {
        console.error('Error al obtener token:', authError);
        message.error('Error de autenticación. Por favor, inicie sesión nuevamente.');
        setBuscando(false);
        return;
      }

      if (!token) {
        message.error('No se pudo obtener el token de autenticación. Por favor, inicie sesión nuevamente.');
        setBuscando(false);
        return;
      }
      
      const datosBusqueda = {
        filtros: filtrosActivos.map(([campo, filtro]) => ({
          campo,
          desde: filtro.desde,
          hasta: filtro.hasta
        }))
      };

      // Validar que los datos de búsqueda sean válidos
      const hasInvalidData = datosBusqueda.filtros.some(f => 
        (f.desde === null && f.hasta === null) || 
        (f.desde !== null && f.hasta !== null && f.desde > f.hasta)
      );

      if (hasInvalidData) {
        message.error('Los datos de búsqueda contienen valores inválidos. Por favor, verifique los filtros.');
        setBuscando(false);
        return;
      }
      
      // Usar fetch directamente como en main
      const response = await fetch(`${process.env.REACT_APP_CLASIFICACION_URL}/seleccion_fardos/${id_empresa}/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(datosBusqueda)
      });

      // Validar que la respuesta sea exitosa
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Error del servidor';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error || errorData.detail || errorMessage;
        } catch {
          errorMessage = errorText || `Error ${response.status}: ${response.statusText}`;
        }
        
        if (response.status === 401 || response.status === 403) {
          message.error('No tiene permisos para realizar esta operación. Por favor, contacte al administrador.');
        } else if (response.status === 404) {
          message.error('El servicio solicitado no fue encontrado. Por favor, contacte al administrador.');
        } else if (response.status >= 500) {
          message.error(`Error del servidor (${response.status}). Por favor, intente nuevamente más tarde.`);
        } else {
          message.error(`Error ${response.status}: ${errorMessage}`);
        }
        setBuscando(false);
        return;
      }

      const data = await response.json();
      const resultados = data.resultados || [];
      setResultados(resultados);
      
      const autoSelectableFardos = resultados.filter((r: any) => shouldAutoSelectFardo(r)) || [];
      const autoSelectableIds = autoSelectableFardos.map((r: any) => r.id);
      setFardosSeleccionados(new Set(autoSelectableIds));
      
      const selectableCount = resultados.filter((r: any) => canSelectFardo(r)).length || 0;
      message.success(`Búsqueda completada. Se encontraron ${resultados.length} resultados (${selectableCount} disponibles para selección)`);
      
      // Contraer los filtros después de la búsqueda exitosa
      if (resultados.length > 0) {
        setFiltrosExpandidos(false);
      }
    } catch (error: any) {
      console.error('Error en la búsqueda:', error);
      
      // Manejo de errores con fetch
      if (error instanceof TypeError && error.message.includes('fetch')) {
        message.error({
          content: 'Error de conexión: No se pudo conectar con el servidor. Verifique su conexión a internet, que el servidor esté disponible e intente nuevamente.',
          duration: 6
        });
      } else if (error.message) {
        message.error({
          content: `Error: ${error.message || 'Error desconocido. Por favor, intente nuevamente.'}`,
          duration: 5
        });
      } else {
        message.error({
          content: 'Ocurrió un error inesperado. Por favor, intente nuevamente o contacte al administrador si el problema persiste.',
          duration: 5
        });
      }
    } finally {
      setBuscando(false);
    }
  };

  const handleToggleAll = (checked: boolean) => {
    const newFilters = { ...filters };
    Object.keys(newFilters).forEach(field => {
      newFilters[field as keyof FilterValues] = {
        ...newFilters[field as keyof FilterValues],
        checked
      };
    });
    setFilters(newFilters);
  };

  // Función para determinar si un fardo puede ser seleccionado
  const canSelectFardo = (resultado: any) => {
    return resultado.STOCK === 'SI' && 
           resultado.APLICADO_COMPRAS === 'SI' && 
           resultado.APLICADO_VENTAS === 'NO';
  };

  // Función para determinar si un fardo debe estar marcado automáticamente
  const shouldAutoSelectFardo = (resultado: any) => {
    return canSelectFardo(resultado);
  };

  const handleFardoSeleccionado = (fardoId: number, checked: boolean) => {
    setFardosSeleccionados(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(fardoId);
      } else {
        newSet.delete(fardoId);
      }
      return newSet;
    });
  };

  const handleSeleccionarTodosFardos = (checked: boolean) => {
    if (checked) {
      const todosIds = resultados.map(r => r.id);
      setFardosSeleccionados(new Set(todosIds));
    } else {
      setFardosSeleccionados(new Set());
    }
  };

  const allChecked = Object.values(filters).every(filter => filter.checked);
  const someChecked = Object.values(filters).some(filter => filter.checked);

  // Cargar rangos HVI al inicio
  useEffect(() => {
    const cargarRangosHVI = async () => {
      try {
        setLoading(true);
        const id_empresa = user?.id_empresa || 0;
        const token = await getAccessTokenSilently();
        
        const response = await apiClient.get<{
          rangos_hvi: Record<string, { min: number; max: number }>;
          calidad_numerica_desde?: number;
          calidad_numerica_hasta?: number;
        }>(
          `${process.env.REACT_APP_CLASIFICACION_URL}/desde_hasta_calidad_hvi/${id_empresa}/`,
          { token }
        );

        const data = response.data;
        setRangosHVI(data.rangos_hvi);
        
        if (data.rangos_hvi) {
          const nuevosFiltros = { ...filters };
          
          if (data.calidad_numerica_desde !== undefined && data.calidad_numerica_hasta !== undefined) {
            nuevosFiltros.CALIDAD_NUMERICA = {
              ...nuevosFiltros.CALIDAD_NUMERICA,
              desde: data.calidad_numerica_desde,
              hasta: data.calidad_numerica_hasta
            };
          }
          
          Object.keys(data.rangos_hvi).forEach(campo => {
            if (nuevosFiltros[campo as keyof FilterValues]) {
              nuevosFiltros[campo as keyof FilterValues] = {
                ...nuevosFiltros[campo as keyof FilterValues],
                desde: data.rangos_hvi[campo].min,
                hasta: data.rangos_hvi[campo].max
              };
            }
          });
          setFilters(nuevosFiltros);
        }
      } catch (error) {
        console.error('Error al cargar rangos HVI:', error);
        message.error('Error al cargar los rangos HVI');
      } finally {
        setLoading(false);
      }
    };

    cargarRangosHVI();
  }, []);

  // Función para renderizar tabla de filtros (actualizada)
  const renderFilterTable = (fields: (keyof FilterValues)[]) => {
    const tableData = fields.map(field => {
      const filter = filters[field];
      const label = field === 'CALIDAD_NUMERICA' ? 'Calidad Numérica' : field;
      const description = hviDescriptions[field] || 'Parámetro HVI';
      const isValid = isValidRange(filter.desde, filter.hasta);

      return {
        key: field,
        field,
        label,
        description,
        filter,
        isValid
      };
    });

    const columns = [
      {
        title: '',
        key: 'checkbox',
        width: 25,
        render: (_: any, record: typeof tableData[0]) => (
          <Checkbox
            checked={record.filter.checked}
            onChange={(e) => handleCheckboxChange(record.field, e.target.checked)}
          />
        )
      },
      {
        title: 'Propiedad',
        key: 'property',
        width: 100,
        render: (_: any, record: typeof tableData[0]) => (
          <Space size={4}>
            <Text style={{ fontSize: '12px', fontWeight: 500 }}>{record.label}</Text>
            <Tooltip title={record.description}>
              <InfoCircleOutlined style={{ color: theme.colors.textSecondary, fontSize: '11px', cursor: 'help' }} />
            </Tooltip>
          </Space>
        )
      },
      {
        title: 'Desde',
        key: 'desde',
        width: 85,
        render: (_: any, record: typeof tableData[0]) => (
          <InputNumber
            placeholder="Desde"
            value={record.filter.desde}
            onChange={(value) => handleDesdeChange(record.field, value)}
            style={{ width: '100%', fontSize: '12px' }}
            precision={2}
            disabled={!record.filter.checked}
            size="small"
            status={record.filter.checked && !record.isValid ? 'error' : undefined}
          />
        )
      },
      {
        title: 'Hasta',
        key: 'hasta',
        width: 85,
        render: (_: any, record: typeof tableData[0]) => (
          <div>
            <InputNumber
              placeholder="Hasta"
              value={record.filter.hasta}
              onChange={(value) => handleHastaChange(record.field, value)}
              style={{ width: '100%', fontSize: '12px' }}
              precision={2}
              disabled={!record.filter.checked}
              size="small"
              status={record.filter.checked && !record.isValid ? 'error' : undefined}
            />
            {record.filter.checked && !record.isValid && (
              <div style={{ marginTop: '2px', fontSize: '10px', color: theme.colors.error }}>
                ⚠️ Rango inválido
              </div>
            )}
          </div>
        )
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
        bordered
        style={{ 
          backgroundColor: 'white',
          fontSize: '12px'
        }}
        rowClassName={(record) => {
          if (record.filter.checked && !record.isValid) return 'error-row';
          if (record.filter.checked) return 'active-row';
          return '';
        }}
      />
    );
  };

  return (
    <div style={{ padding: '0' }}>
      <style>{`
        .error-row {
          background-color: ${theme.colors.error}15 !important;
        }
        .error-row:hover {
          background-color: ${theme.colors.error}25 !important;
        }
        .ant-table-tbody > tr.error-row > td {
          border-color: ${theme.colors.error}40 !important;
        }
        .active-row {
          background-color: ${theme.colors.backgroundGray} !important;
        }
        .active-row:hover {
          background-color: ${theme.colors.backgroundGray} !important;
        }
      `}</style>
      <PageTitle>Filtros de Clasificación HVI</PageTitle>
      
      {loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm,
          border: `1px solid ${theme.colors.borderLight}`,
          marginBottom: '24px'
        }}>
          <Text type="secondary">Cargando rangos de datos...</Text>
        </div>
      )}
      
      {/* Configuración de Filtros HVI */}
      <div style={{ 
        marginBottom: '24px',
        padding: '20px',
        backgroundColor: 'white',
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.sm,
        border: `1px solid ${theme.colors.borderLight}`
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <Space size="middle">
            <Text strong style={{ fontSize: '15px', color: theme.colors.primaryDark }}>
              Propiedades HVI
            </Text>
            {activeFilters.length > 0 && (
              <Text type="secondary" style={{ fontSize: '13px' }}>
                Filtros Activos <Tag color="blue" style={{ marginLeft: '4px' }}>{activeFilters.length}</Tag>
              </Text>
            )}
          </Space>
          <Space size="middle">
            <Checkbox
              indeterminate={someChecked && !allChecked}
              checked={allChecked}
              onChange={(e) => handleToggleAll(e.target.checked)}
              style={{ fontSize: '14px', fontWeight: 600 }}
            >
              {allChecked ? 'Desmarcar Todos' : 'Marcar Todos'}
            </Checkbox>
            {rangosHVI && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ✓ {Object.keys(rangosHVI).length} campos HVI disponibles
              </Text>
            )}
            <Button
              type="default"
              icon={filtrosExpandidos ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setFiltrosExpandidos(!filtrosExpandidos)}
              size="small"
              style={{ fontSize: '12px' }}
            >
              {filtrosExpandidos ? 'Ocultar Filtros' : 'Mostrar Filtros'}
            </Button>
          </Space>
        </div>
        <Tabs
          defaultActiveKey="calidad"
          type="card"
          size="small"
          tabBarExtraContent={{
            right: (
              <Button 
                type="primary" 
                icon={<SearchOutlined />}
                onClick={handleSearch}
                size="small"
                loading={buscando}
                style={{ 
                  minWidth: '100px',
                  height: '28px',
                  backgroundColor: theme.colors.secondary,
                  borderColor: theme.colors.secondary,
                  fontSize: '13px',
                  fontWeight: 500,
                  marginLeft: '8px'
                }}
              >
                Buscar
              </Button>
            )
          }}
          items={Object.entries(filterCategories).map(([key, category]) => ({
            key,
            label: (
              <Space>
                <span>{category.icon}</span>
                <span>{category.shortLabel}</span>
                <Badge 
                  count={category.fields.filter(f => filters[f].checked).length} 
                  showZero 
                  style={{ backgroundColor: theme.colors.secondary }}
                />
              </Space>
            ),
            children: filtrosExpandidos ? (
              <div style={{ padding: '16px 0' }}>
                {key === 'avanzadas' ? (
                  // Para Avanzadas: dividir en 2 tablas lado a lado
                  <Row gutter={16}>
                    <Col span={12}>
                      {renderFilterTable(category.fields.slice(0, 5))}
                    </Col>
                    <Col span={12}>
                      {renderFilterTable(category.fields.slice(5))}
                    </Col>
                  </Row>
                ) : (
                  // Para las demás categorías: una tabla con ancho limitado para que coincida con Avanzadas
                  <Row>
                    <Col span={12}>
                      {renderFilterTable(category.fields)}
                    </Col>
                  </Row>
                )}
              </div>
            ) : null
          }))}
        />
      </div>
      
      {/* Grid de resultados */}
      {resultados.length > 0 && (
        <div style={{ 
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm,
          border: `1px solid ${theme.colors.borderLight}`
        }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '20px'
          }}>
            <Text strong style={{ fontSize: '16px' }}>Resultado de Búsqueda</Text>
            <Tag color="success" style={{ fontSize: '13px', padding: '4px 12px' }}>
              {resultados.filter(r => canSelectFardo(r)).length} de {resultados.length} disponibles
            </Tag>
          </div>
          
          <div style={{ 
            marginBottom: '16px',
            padding: '12px',
            backgroundColor: theme.colors.backgroundGray,
            borderRadius: theme.borderRadius.md
          }}>
            <Text type="secondary" style={{ fontSize: '13px' }}>
              Fardos disponibles para selección: STOCK=SI, APLICADO_COMPRAS=SI, APLICADO_VENTAS=NO
            </Text>
          </div>
        
          <style>{`
            .ant-table-thead > tr > th {
              font-size: 11px !important;
              padding: 8px 4px !important;
            }
            .ant-table-tbody > tr > td {
              font-size: 11px !important;
              padding: 6px 4px !important;
            }
          `}</style>
          <Table
            dataSource={resultados}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: 'max-content', y: 400 }}
            size="small"
            bordered
            style={{ fontSize: '11px' }}
            rowSelection={{
              type: 'checkbox',
              selectedRowKeys: Array.from(fardosSeleccionados),
              onChange: (selectedRowKeys) => {
                const numericKeys = selectedRowKeys.map(key => Number(key));
                setFardosSeleccionados(new Set(numericKeys));
              },
              onSelectAll: (selected) => {
                if (selected) {
                  const selectableIds = resultados
                    .filter(resultado => canSelectFardo(resultado))
                    .map(r => r.id);
                  setFardosSeleccionados(new Set(selectableIds));
                } else {
                  setFardosSeleccionados(new Set());
                }
              },
              getCheckboxProps: (record) => ({
                disabled: !canSelectFardo(record),
                checked: fardosSeleccionados.has(record.id)
              })
            }}
            columns={[
              {
                title: <span style={{ fontSize: '11px' }}>Nro Fardo</span>,
                dataIndex: 'numero_fardo',
                key: 'numero_fardo',
                width: 70,
                align: 'center',
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value}</span>
              },
              {
                title: <span style={{ fontSize: '11px' }}>Origen</span>,
                dataIndex: 'origen',
                key: 'origen',
                width: 100,
                ellipsis: true,
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value}</span>
              },
              {
                title: <span style={{ fontSize: '11px' }}>Planilla Nro</span>,
                dataIndex: 'numero_planilla',
                key: 'numero_planilla',
                width: 80,
                align: 'center',
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value}</span>
              },
              {
                title: <span style={{ fontSize: '11px' }}>Fecha</span>,
                dataIndex: 'fecha_clasificacion',
                key: 'fecha_clasificacion',
                width: 90,
                align: 'center',
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value}</span>
              },
              {
                title: <span style={{ fontSize: '11px' }}>Calidad</span>,
                dataIndex: 'calidad',
                key: 'calidad',
                width: 70,
                align: 'center',
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value}</span>
              },
              {
                title: <span style={{ fontSize: '11px' }}>STOCK</span>,
                dataIndex: 'STOCK',
                key: 'STOCK',
                width: 65,
                align: 'center',
                render: (value: string) => (
                  <Tag color={value === 'SI' ? 'success' : 'error'} style={{ fontSize: '10px', padding: '2px 6px', margin: 0 }}>
                    {value}
                  </Tag>
                )
              },
              {
                title: <span style={{ fontSize: '11px' }}>Aplicado Compras</span>,
                dataIndex: 'APLICADO_COMPRAS',
                key: 'APLICADO_COMPRAS',
                width: 100,
                align: 'center',
                render: (value: string) => (
                  <Tag color={value === 'SI' ? 'success' : 'error'} style={{ fontSize: '10px', padding: '2px 6px', margin: 0 }}>
                    {value}
                  </Tag>
                )
              },
              {
                title: <span style={{ fontSize: '11px' }}>Aplicado Ventas</span>,
                dataIndex: 'APLICADO_VENTAS',
                key: 'APLICADO_VENTAS',
                width: 100,
                align: 'center',
                render: (value: string) => (
                  <Tag color={value === 'SI' ? 'success' : 'error'} style={{ fontSize: '10px', padding: '2px 6px', margin: 0 }}>
                    {value}
                  </Tag>
                )
              },
              ...Object.keys(filters).filter(campo => 
                campo !== 'CALIDAD_NUMERICA' && filters[campo as keyof FilterValues].checked
              ).map(campo => ({
                title: <span style={{ fontSize: '11px' }}>{campo}</span>,
                dataIndex: campo,
                key: campo,
                width: 70,
                align: 'center' as const,
                render: (value: any) => <span style={{ fontSize: '11px' }}>{value !== null && value !== undefined ? value : 'N/A'}</span>
              }))
            ]}
          />
          
          {/* Botón de procesamiento */}
          {fardosSeleccionados.size > 0 && (
            <div style={{ 
              marginTop: '20px',
              padding: '16px',
              backgroundColor: theme.colors.backgroundGray,
              borderRadius: theme.styles.borderRadius
            }}>
              <Row gutter={[16, 8]} justify="center" align="middle">
                <Col>
                  <Text strong style={{ marginRight: '8px', fontSize: '14px' }}>
                    Sumar al Lote Nº:
                  </Text>
                  <InputNumber
                    placeholder="Número de lote"
                    value={numeroLote}
                    onChange={(value) => setNumeroLote(value)}
                    min={1}
                    size="small"
                    style={{ width: '150px' }}
                  />
                </Col>
                <Col>
                  <Button 
                    type="primary" 
                    size="small"
                    disabled={!numeroLote || numeroLote < 1}
                    onClick={async () => {
                      try {
                        const token = await getAccessTokenSilently();
                        const id_empresa = user?.id_empresa || 1;
                        const clasificacion_detalle_ids = Array.from(fardosSeleccionados);
                        
                        const requestData = {
                          id_empresa,
                          numero_lote: numeroLote,
                          clasificacion_detalle_ids
                        };
                        
                        await apiClient.post(
                          `${process.env.REACT_APP_CLASIFICACION_URL}/actualizar_lote/`,
                          requestData,
                          { token }
                        );
                        
                        message.success(`Se actualizaron ${clasificacion_detalle_ids.length} fardos al lote ${numeroLote}`);
                        setFardosSeleccionados(new Set());
                        setNumeroLote(null);
                      } catch (error) {
                        console.error('Error en la actualización:', error);
                        message.error('Error al actualizar el lote');
                      }
                    }}
                    style={{
                      backgroundColor: theme.colors.secondary,
                      borderColor: theme.colors.secondary,
                      minWidth: '200px'
                    }}
                  >
                    Procesar Fardos Seleccionados ({fardosSeleccionados.size})
                  </Button>
                </Col>
              </Row>
            </div>
          )}
        </div>
      )}
      
      {resultados.length === 0 && !loading && (
        <div style={{ 
          textAlign: 'center', 
          padding: '60px 40px',
          backgroundColor: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm,
          border: `1px solid ${theme.colors.borderLight}`,
          marginTop: '24px'
        }}>
          <Text type="secondary" style={{ fontSize: '14px' }}>
            No hay resultados. Configure los filtros y haga clic en "Buscar" para ver los fardos.
          </Text>
        </div>
      )}
    </div>
  );
};

export default Loteo;
